import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { useQueryClient } from "@tanstack/react-query";

const blogArticles = [
  { title: "How to Travel Sustainably: Tips for Eco-Friendly Adventures", category: "Sustainable Travel" },
  { title: "Zero-Waste Packing: Essentials to Reduce Your Travel Footprint", category: "Sustainable Travel" },
  { title: "Solo Travel Destinations for First-Time Adventurers", category: "Solo Travel" },
  { title: "Benefits of Solo Travel for Mental Health and Personal Growth", category: "Solo Travel" },
  { title: "10 Things Most Travelers Forget to Pack (and How to Avoid It)", category: "Smart Packing" },
  { title: "How to Pack Light Without Leaving the Essentials Behind", category: "Smart Packing" },
  { title: "How to Organize Your Suitcase for Easy Access on the Go", category: "Smart Packing" },
  { title: "What to Wear for a Comfortable Long-Haul Flight", category: "Comfortable Travel" },
  { title: "Must-Have Travel Accessories for Comfort and Convenience", category: "Comfortable Travel" },
  { title: "How to Manage Jet Lag and Rest During Travel", category: "Comfortable Travel" },
  { title: "Staying Healthy While Traveling: Nutrition and Exercise Tips", category: "Wellness on the Road" },
  { title: "Mindfulness and Meditation Techniques for Stress-Free Travel", category: "Wellness on the Road" },
  { title: "Must-Have Travel Apps for a Seamless Trip", category: "Tech-Enhanced Travel" },
  { title: "Best Mobile Devices and Tech Gear for Travelers", category: "Tech-Enhanced Travel" },
  { title: "Digital Nomad Lifestyle: Balancing Work and Travel", category: "Special Interest" },
  { title: "Future of Travel: What to Expect in the Next 5 Years", category: "Trends" },
  { title: "How to Find the Best Flight Deals in 2025", category: "Booking & Planning" },
  { title: "Hacks for Booking Cheaper Hotels or Airbnbs", category: "Booking & Planning" },
  { title: "When Is the Best Time to Book Flights?", category: "Booking & Planning" },
  { title: "Essential Safety Tips for Solo & Group Travelers", category: "Travel Safety" },
  { title: "Where to Travel Each Season: Complete Guide", category: "Seasonal Content" },
  { title: "Trending Destinations of 2025", category: "Trending" },
  { title: "Where to Go for a Traditional Winter Christmas Celebration", category: "Seasonal Content" },
  { title: "Best Destinations for a Hot Summer Christmas Getaway", category: "Seasonal Content" },
];

export default function GenerateBlogPosts() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const { toast } = useToast();

  // Already published posts
  const { data: existingPosts = [] } = useBlogPosts();

  // Compute which predefined articles are still missing
  const existingTitles = useMemo(() => new Set(existingPosts.map((p) => p.title)), [existingPosts]);
  const missingArticles = useMemo(
    () => blogArticles.filter((a) => !existingTitles.has(a.title)),
    [existingTitles]
  );


  const handleGenerate = async () => {
    setIsGenerating(true);
    setResults([]);

    try {
      const { data, error } = await supabase.functions.invoke('generate-blog-posts', {
        body: { articles: blogArticles }
      });

      if (error) throw error;

      setResults(data.results);
      
      const successCount = data.results.filter((r: any) => r.success).length;
      toast({
        title: "Generation Complete",
        description: `Successfully generated ${successCount} out of ${blogArticles.length} articles.`,
      });
    } catch (error: any) {
      console.error('Error generating blog posts:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate blog posts",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateMissing = async () => {
    if (missingArticles.length === 0) {
      toast({ title: "Nothing to generate", description: "All predefined articles are already published." });
      return;
    }

    setIsGenerating(true);
    setResults([]);

    try {
      let success = 0;
      for (const article of missingArticles) {
        const { data, error } = await supabase.functions.invoke('generate-blog-posts', {
          body: { articles: [article] }
        });
        if (error) {
          setResults((prev) => [...prev, { title: article.title, success: false, error: error.message }]);
        } else {
          const item = data?.results?.[0];
          const ok = item?.success;
          if (ok) success += 1;
          setResults((prev) => [...prev, item ?? { title: article.title, success: !!ok }]);
        }
        // Small delay to avoid rate limits
        await new Promise((r) => setTimeout(r, 1200));
      }

      toast({
        title: "Generation Complete",
        description: `Successfully generated ${success} out of ${missingArticles.length} missing articles.`,
      });
    } catch (error: any) {
      console.error('Error generating missing blog posts:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate some blog posts",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="p-6">
        <h1 className="text-2xl font-bold mb-4">Generate Blog Posts</h1>
        <p className="text-muted-foreground mb-6">
          This will generate {blogArticles.length} blog articles using AI and add them to your blog.
          The process may take several minutes.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating}
            size="lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Articles...
              </>
            ) : (
              'Generate All Blog Posts'
            )}
          </Button>

          <Button
            onClick={handleGenerateMissing}
            disabled={isGenerating || missingArticles.length === 0}
            variant="secondary"
          >
            {isGenerating ? 'Please wait...' : `Generate Missing Only (${missingArticles.length})`}
          </Button>
        </div>


        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Results:</h2>
            <div className="space-y-2">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg ${
                    result.success ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
                  }`}
                >
                  <p className="font-medium">{result.title}</p>
                  {result.error && (
                    <p className="text-sm mt-1">Error: {result.error}</p>
                  )}
                  {result.success && (
                    <p className="text-sm mt-1">✓ Successfully created</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
